import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, MapPin, User, CheckCircle2, ArrowRight, ArrowLeft, Smartphone, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
import Button from '../common/Button';
import api from '../../services/api';
//import orderService from '../../services/orderService';
const Checkout = () => {
    const { cart, cartTotal, clearCart } = useCart();
    const { user } = useUser();
    const navigate = useNavigate();
    const [step, setStep] = useState(1); // 1: Info, 2: Payment, 3: Success
    const [paymentMethod, setPaymentMethod] = useState('');
    //const [paymentPhone, setPaymentPhone] = useState(''); // Phone for payment
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        fullName: '',
        phone: '',
        address: '',
        city: ''
    });

    useEffect(() => {
        if (!user) {
            navigate('/auth');
        }
    }, [user, navigate]);

    if (!user) return null;


    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleNextStep = () => {
        if (step === 1) setStep(2);
    };

    const handlePlaceOrder = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const orderData = {
                user: user.id,
                full_name: formData.fullName,
                email: user.email,
                address: formData.address,
                city: formData.city,
                phone: formData.phone,
                payment_method: paymentMethod === 'whatsapp' ? 'WhatsApp' : 'COD',
                items: cart.map(item => ({
                    product: item.id,
                    quantity: parseInt(item.quantity, 10),
                }))
            };

            // 1. Create the order
            await api.post('/api/commandes/', orderData);

            if (paymentMethod === 'cod') {
                // Cash on Delivery
                setStep(3);
                clearCart();
                setIsLoading(false);
            } else if (paymentMethod === 'whatsapp') {
                // WhatsApp
                const phoneNumber = "221781960532"; 

                const itemsList = cart.map(item => {
                    const productLink = `${window.location.origin}/product/${item.id}`;
                    return `- ${item.quantity}x ${item.name} (${Math.floor(item.price * item.quantity).toLocaleString('fr-FR')} Fcfa)\n  🔗 Lien : ${productLink}`;
                }).join('\n');

                const message = `Bonjour,\nJe suis intéressé(e) par ces articles :\n\n${itemsList}\n\n*Total : ${Math.floor(cartTotal).toLocaleString('fr-FR')} Fcfa*\n\n*Mes informations :*\n👤 Nom : ${formData.fullName}\n📍 Adresse : ${formData.address}, ${formData.city}\n📞 Téléphone : ${formData.phone}\n\nJ'aimerais finaliser l'achat avec vous.`;

                const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

                window.open(whatsappUrl, '_blank');
                api.post('/api/stats/whatsapp/').catch(e => console.error('Stats error:', e));
                
                setStep(3);
                clearCart();
                setIsLoading(false);
            }
        } catch (err) {
            console.error('Error placing order:', err);
            setError("Une erreur est survenue lors de la validation de votre commande. Veuillez réessayer.");
            setIsLoading(false);
        }
    };

    if (step === 3) {
        return (
            <div className="min-h-[80vh] flex flex-col items-center justify-center pt-32 pb-24 text-center px-6">
                <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-8 animate-in zoom-in duration-700">
                    <CheckCircle2 className="w-12 h-12" />
                </div>
                <h1 className="text-4xl font-extrabold text-green-950 mb-4">
                    {paymentMethod === 'whatsapp' ? 'Message Envoyé !' : 'Commande Confirmée !'}
                </h1>
                <p className="text-lg text-slate-600 mb-10 max-w-md">
                    {paymentMethod === 'whatsapp'
                        ? `Merci ${formData.fullName}. Nous poursuivrons l'échange sur WhatsApp pour finaliser votre commande.`
                        : `Merci ${formData.fullName} pour votre confiance. Votre commande est en cours de préparation et sera livrée à ${formData.address}.`}
                </p>
                <div className="p-8 rounded-[2rem] bg-green-50/50 border border-green-100 mb-10 text-left w-full max-w-md">
                    <p className="text-xs font-bold text-green-500 uppercase tracking-widest mb-4">Récapitulatif de livraison</p>
                    <div className="space-y-3 text-sm">
                        <p className="flex justify-between">
                            <span className="text-slate-500">Contact :</span>
                            <span className="font-bold text-green-950">{formData.phone}</span>
                        </p>
                        <p className="flex justify-between">
                            <span className="text-slate-500">Mode de paiement :</span>
                            <span className="font-bold text-green-950 uppercase">{paymentMethod}</span>
                        </p>
                    </div>
                </div>
                <Link to="/">
                    <Button variant="primary" className="h-14 px-10 text-lg">
                        Retour à l'accueil
                    </Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-white pt-32 pb-24 min-h-screen">
            <div className="container mx-auto px-6">
                {/* Stepper Header */}
                <div className="max-w-4xl mx-auto mb-16">
                    <div className="flex items-center justify-between relative">
                        {/* Progress Line */}
                        <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-green-100 -translate-y-1/2 z-0"></div>
                        <div
                            className="absolute top-1/2 left-0 h-0.5 bg-green-600 -translate-y-1/2 z-0 transition-all duration-500"
                            style={{ width: step === 1 ? '0%' : '100%' }}
                        ></div>

                        {/* Step Dots */}
                        <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${step >= 1 ? 'bg-green-600 text-white shadow-lg' : 'bg-white text-slate-300 border-2 border-slate-100'}`}>1</div>
                        <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${step >= 2 ? 'bg-green-600 text-white shadow-lg' : 'bg-white text-slate-300 border-2 border-slate-100'}`}>2</div>
                    </div>
                    <div className="flex justify-between mt-4 px-2">
                        <span className="text-sm font-bold text-green-900 uppercase tracking-widest">Livraison</span>
                        <span className="text-sm font-bold text-slate-300 uppercase tracking-widest">Paiement</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start max-w-6xl mx-auto">
                    {/* Left Column: Forms */}
                    <div className="space-y-10">
                        {step === 1 ? (
                            <div className="animate-in fade-in slide-in-from-left-5 duration-500">
                                <h1 className="text-3xl font-extrabold text-green-950 mb-8">Informations de livraison</h1>
                                <form className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="relative">
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Nom complet</label>
                                            <div className="relative">
                                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                                                <input
                                                    type="text"
                                                    name="fullName"
                                                    value={formData.fullName}
                                                    onChange={handleInputChange}
                                                    placeholder="Prénom et Nom"
                                                    className="w-full h-14 pl-12 pr-6 rounded-2xl bg-green-50/50 border border-green-100 focus:border-green-600 focus:ring-0 transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                        <div className="relative">
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Téléphone</label>
                                            <div className="relative">
                                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={formData.phone}
                                                    onChange={handleInputChange}
                                                    placeholder="7x xxx xx xx"
                                                    className="w-full h-14 pl-12 pr-6 rounded-2xl bg-green-50/50 border border-green-100 focus:border-green-600 focus:ring-0 transition-all outline-none"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Adresse précise</label>
                                        <div className="relative">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300" />
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleInputChange}
                                                placeholder="Rue, Quartier, Indications..."
                                                className="w-full h-14 pl-12 pr-6 rounded-2xl bg-green-50/50 border border-green-100 focus:border-green-600 focus:ring-0 transition-all outline-none"
                                            />
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2 block">Ville</label>
                                        <input
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            onChange={handleInputChange}
                                            placeholder="Ex: Dakar"
                                            className="w-full h-14 px-6 rounded-2xl bg-green-50/50 border border-green-100 focus:border-green-600 focus:ring-0 transition-all outline-none"
                                        />
                                    </div>
                                </form>
                                <Button
                                    variant="primary"
                                    className="w-full h-16 mt-10 text-lg"
                                    onClick={handleNextStep}
                                    disabled={!formData.fullName || !formData.phone || !formData.address}
                                >
                                    Continuer vers le paiement
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </Button>
                            </div>
                        ) : (
                            <div className="animate-in fade-in slide-in-from-right-5 duration-500">
                                <h1 className="text-3xl font-extrabold text-green-950 mb-2">Moyen de paiement</h1>
                                <p className="text-slate-500 mb-8">Choisissez comment vous souhaitez régler votre commande.</p>

                                <>
                                    {error && (
                                        <p className="mb-4 text-red-500 text-sm font-bold text-center">{error}</p>
                                    )}
                                    <div className="space-y-4">
                                        <button
                                            onClick={() => setPaymentMethod('whatsapp')}
                                            className={`w-full flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all group ${paymentMethod === 'whatsapp' ? 'border-green-500 bg-green-50' : 'border-slate-100 hover:border-green-200 bg-white'}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-200">
                                                    <Smartphone className="w-6 h-6" />
                                                </div>
                                                <div className="text-left">
                                                    <p className="font-bold text-green-950">Commander via WhatsApp</p>
                                                    <p className="text-xs text-slate-500">Contact direct avec notre équipe pour le paiement</p>
                                                </div>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'whatsapp' ? 'border-green-500 bg-green-500' : 'border-slate-200'}`}>
                                                {paymentMethod === 'whatsapp' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                            </div>
                                        </button>

                                        <button
                                            onClick={() => setPaymentMethod('cod')}
                                            className={`w-full flex items-center justify-between p-6 rounded-[2rem] border-2 transition-all group ${paymentMethod === 'cod' ? 'border-green-600 bg-green-50' : 'border-slate-100 hover:border-green-200 bg-white'}`}
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-green-200">
                                                    <Truck className="w-6 h-6" />
                                                </div>
                                                <div className="text-left">
                                                    <p className="font-bold text-green-950">Paiement à la livraison</p>
                                                    <p className="text-xs text-slate-500">Payez en espèces dès réception</p>
                                                </div>
                                            </div>
                                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'cod' ? 'border-green-600 bg-green-600' : 'border-slate-200'}`}>
                                                {paymentMethod === 'cod' && <div className="w-2 h-2 rounded-full bg-white"></div>}
                                            </div>
                                        </button>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4 mt-10">
                                        <button
                                            onClick={() => setStep(1)}
                                            className="h-16 px-8 rounded-2xl font-bold text-slate-400 hover:text-green-950 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <ArrowLeft className="w-5 h-5" />
                                            Retour
                                        </button>
                                        <Button
                                            variant="primary"
                                            className="h-16 flex-grow text-lg shadow-green-900/20"
                                            onClick={handlePlaceOrder}
                                            disabled={
                                                !paymentMethod ||
                                                isLoading
                                            }
                                        >
                                            {isLoading ? (
                                                <span className="flex items-center gap-2">
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                    Traitement...
                                                </span>
                                            ) : (
                                                <>
                                                    {paymentMethod === 'cod' ? 'Confirmer la commande' : 'Continuer sur WhatsApp'}
                                                    <ArrowRight className="w-5 h-5 ml-2" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Order Summary */}
                    <div className="w-full">
                        <div className="p-10 rounded-[2.5rem] bg-green-50/50 border border-green-100 shadow-sm">
                            <h2 className="text-2xl font-bold text-green-950 mb-8">Résumé de la commande</h2>

                            <div className="space-y-6 max-h-[300px] overflow-y-auto pr-2 mb-8 custom-scrollbar">
                                {cart.map((item) => (
                                    <div key={item.id} className="flex items-center gap-4">
                                        <div className="w-16 h-16 rounded-xl bg-white overflow-hidden border border-green-100 shrink-0">
                                            <img
                                                src={item.image.startsWith('http') ? item.image : `${api.defaults.baseURL}${item.image}`}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="font-bold text-green-950 text-sm leading-tight mb-1">{item.name}</p>
                                                <p className="text-xs text-slate-500">Qte: {item.quantity}</p>
                                            </div>
                                            <span className="font-bold text-green-900">{Math.floor(item.price * item.quantity).toLocaleString('fr-FR')} Fcfa</span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-4 pt-6 border-t border-green-100">
                                <div className="flex justify-between items-center">
                                    <span className="text-slate-500 font-bold">Sous-total</span>
                                    <span className="font-bold text-green-950">{Math.floor(cartTotal).toLocaleString('fr-FR')} Fcfa</span>
                                </div>
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-slate-500">Livraison</span>
                                    <span className="text-green-600 font-bold">Gratuite</span>
                                </div>
                                <div className="flex justify-between items-end pt-4 border-t border-slate-100 mt-4">
                                    <div>
                                        <span className="block font-black text-green-950 text-lg">Total</span>
                                        <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">TTC</span>
                                    </div>
                                    <span className="font-extrabold text-green-600">{Math.floor(cartTotal).toLocaleString('fr-FR')} Fcfa</span>
                                </div>
                            </div>
                        </div>

                        {step === 1 && (
                            <Link to="/cart" className="flex items-center justify-center gap-2 mt-8 text-slate-400 hover:text-green-600 font-bold transition-all">
                                <ArrowLeft className="w-5 h-5" />
                                Modifier le panier
                            </Link>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Checkout;
